import React, { useEffect, useState } from 'react';
import {
  Copy, Check, ArrowLeft, Store, AlertCircle, CheckCircle2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAlert } from '../../components/AlertProvider';

interface SalesPoint {
  id: number;
  name: string;
  code: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
}

interface PurchaseOrder {
  id: number;
  order_number: string;
  client_id: number;
  total_amount: number;
  subtotal: number;
  currency: string;
  Client?: {
    company_name: string;
  };
  items?: Array<{
    consumable_id: number;
    quantity: number;
    unit_price: number;
    product_title: string;
    product_unit: string;
  }>;
}

const DuplicateOrderTailwind: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { showSuccess, showError } = useAlert();
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
  const [selectedSalesPoints, setSelectedSalesPoints] = useState<Set<number>>(new Set());
  const [copyQuantities, setCopyQuantities] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    void loadData();
  }, [orderId]);

  const loadData = async (): Promise<void> => {
    if (!orderId) return;
    
    setBusy(true);
    try {
      // Charger la commande source
      const resOrder = await api.get(`/purchase-orders/${orderId}`);
      setOrder(resOrder.data);

      // Charger les points de vente du Client
      if (resOrder.data.client_id) {
        const resSP = await api.get(`/clients/${resOrder.data.client_id}/sales-points`);
        setSalesPoints(Array.isArray(resSP.data) ? resSP.data : []);
      }
    } catch (e: any) {
      console.error('Load error:', e);
      showError(e.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setBusy(false);
    }
  };

  const toggleSalesPoint = (id: number): void => {
    const newSet = new Set(selectedSalesPoints);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedSalesPoints(newSet);
  };

  const selectAll = (): void => {
    setSelectedSalesPoints(new Set(salesPoints.map(sp => sp.id)));
  };

  const deselectAll = (): void => {
    setSelectedSalesPoints(new Set());
  };

  const submit = async (): Promise<void> => {
    
    if (selectedSalesPoints.size === 0) {
      showError('Veuillez sélectionner au moins un point de vente');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      selectedSalesPoints.forEach(id => params.append('sales_point_ids', id.toString()));
      params.append('copy_quantities', copyQuantities.toString());

      await api.post(`/purchase-orders/${orderId}/duplicate?${params.toString()}`);
      
      setSuccess(true);
      showSuccess(`Duplication réussie ! ${selectedSalesPoints.size} commande(s) créée(s).`);
      setTimeout(() => {
        navigate('/achat/orders');
      }, 2000);
    } catch (e: any) {
      console.error('Duplicate error:', e);
      showError(e.response?.data?.detail || 'Erreur lors de la duplication');
    } finally {
      setSubmitting(false);
    }
  };

  if (busy) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-700 dark:text-slate-300">Commande non trouvée</p>
          <button onClick={() => navigate('/achat/orders')} className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
            Retour aux commandes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-cyan-50/20 to-slate-50 dark:from-slate-950 dark:via-cyan-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/achat/orders')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Copy className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Dupliquer la commande</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {order.order_number} • {order.Client?.company_name}
            </p>
          </div>
        </div>
        <button 
          onClick={submit}
          disabled={submitting || selectedSalesPoints.size === 0 || success}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 font-medium disabled:cursor-not-allowed"
        >
          {submitting ? 'Duplication...' : success ? 'Terminé !' : 'Dupliquer'}
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          {/* Success */}
          {success && (
            <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">Duplication réussie !</p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  {selectedSalesPoints.size} commande{selectedSalesPoints.size > 1 ? 's' : ''} créée{selectedSalesPoints.size > 1 ? 's' : ''}. Redirection...
                </p>
              </div>
            </div>
          )}

          {/* Erreur - Removed as we use toast now */}
          {/* 
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )} 
          */}

          {/* Résumé de la commande */}
          <div className="p-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <h3 className="font-bold text-lg mb-4">Commande source</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500 dark:text-slate-400">Numéro:</span>
                <p className="font-medium">{order.order_number}</p>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Client:</span>
                <p className="font-medium">{order.Client?.company_name}</p>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Articles:</span>
                <p className="font-medium">{order.items?.length || 0}</p>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Total:</span>
                <p className="font-medium">{(order.subtotal || order.total_amount).toFixed(2)} {order.currency} HT</p>
              </div>
            </div>
            {order.items && order.items.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <h4 className="font-medium text-sm mb-2">Articles:</h4>
                <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                  {order.items.map((item, idx) => (
                    <li key={idx}>
                      • {item.product_title} - {item.quantity} {item.product_unit} × {item.unit_price.toFixed(2)} {order.currency}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="p-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <h3 className="font-bold text-lg mb-4">Options de duplication</h3>
            <label className="flex items-center gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                checked={copyQuantities}
                onChange={(e) => setCopyQuantities(e.target.checked)}
                className="w-5 h-5 text-cyan-600 rounded"
              />
              <div>
                <span className="font-medium">Copier les quantités</span>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Si décoché, les commandes seront créées avec les mêmes articles mais quantité = 0
                </p>
              </div>
            </label>
          </div>

          {/* Sélection des points de vente */}
          <div className="p-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Store className="h-5 w-5 text-cyan-600" />
                Points de vente ({selectedSalesPoints.size} / {salesPoints.length})
              </h3>
              <div className="flex gap-2">
                <button onClick={selectAll} className="px-3 py-1 text-sm bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300 rounded hover:bg-cyan-100 dark:hover:bg-cyan-950/50">
                  Tout sélectionner
                </button>
                <button onClick={deselectAll} className="px-3 py-1 text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
                  Tout désélectionner
                </button>
              </div>
            </div>

            {salesPoints.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <Store className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucun point de vente trouvé pour cet client</p>
                <p className="text-sm mt-1">Créez d'abord des points de vente pour pouvoir dupliquer cette commande</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {salesPoints.map(sp => {
                  const isSelected = selectedSalesPoints.has(sp.id);
                  return (
                    <label 
                      key={sp.id}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/20' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-cyan-300 dark:hover:border-cyan-700'
                      }`}
                    >
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => toggleSalesPoint(sp.id)}
                        className="mt-1 w-5 h-5 text-cyan-600 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{sp.name}</h4>
                          {sp.code && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                              {sp.code}
                            </span>
                          )}
                          {isSelected && (
                            <Check className="h-4 w-4 text-cyan-600 ml-auto" />
                          )}
                        </div>
                        {(sp.address || sp.city) && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            {sp.address && `${sp.address}, `}
                            {sp.postal_code} {sp.city}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Récapitulatif */}
          {selectedSalesPoints.size > 0 && (
            <div className="p-6 rounded-xl border-2 border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950/20">
              <h3 className="font-bold text-lg mb-2 text-cyan-900 dark:text-cyan-100">Récapitulatif</h3>
              <p className="text-sm text-cyan-800 dark:text-cyan-200">
                <strong>{selectedSalesPoints.size}</strong> commande{selectedSalesPoints.size > 1 ? 's' : ''} 
                {' '}sera{selectedSalesPoints.size > 1 ? 'ont' : ''} créée{selectedSalesPoints.size > 1 ? 's' : ''} en brouillon
                {copyQuantities ? ' avec les quantités copiées' : ' avec quantités à 0'}.
              </p>
              <p className="text-sm text-cyan-700 dark:text-cyan-300 mt-1">
                Vous pourrez les modifier individuellement avant de les confirmer.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicateOrderTailwind;
